import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { saveAs } from 'file-saver';
import {
  Download,
  Upload,
  Users,
  TrendingUp,
  TrendingDown,
  Clock,
  UserCheck,
  PhoneCall
} from 'lucide-react';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import PaginationComponent from '../../shared/components/PaginationComponent';
import Modal from '../../shared/components/ui/Modal';
import UploadResultsModal from '../components/ui/UploadResultsModal';
import PreleadQualifyConfirmModal from '../components/ui/PreleadQualifyConfirmModal';
import PreleadLogModal, { PreleadNoteButton } from '../components/ui/PreleadLogModal';
import { normalizeTemplateUploadResponse } from '../utils/templateUploadResponse';
import {
  createLead,
  downloadPreLeadTemplate,
  getPreLeadMetaFields,
  getPreLeadsByListId,
  markPreLeadAsUnqualified,
  uploadPreLeadTemplate
} from '../services/api/implementation/impl';
import { capitalizeWords, formatStatusLabel } from '../utils/stringUtils';
import {
  getPreleadListFilterQuery,
  normalizePreleadListFilter,
  PRELEAD_LIST_FILTER_CONFIG,
  PRELEAD_LIST_FILTER_TABS
} from '../utils/preleadListFilters';

function formatCellDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function normalizePreleadState(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
}

function getRelevantCampaignSend(sends, preleadState) {
  if (!Array.isArray(sends) || sends.length === 0) return null;
  const normalizedState = normalizePreleadState(preleadState);
  const byLatestSentAt = (a, b) => new Date(b?.sent_at ?? 0) - new Date(a?.sent_at ?? 0);
  const latestSend = [...sends].sort(byLatestSentAt)[0];
  const interestedSend = sends
    .filter((send) => normalizePreleadState(send?.state) === 'interested')
    .sort(byLatestSentAt)[0];
  const notInterestedSend = sends
    .filter((send) => normalizePreleadState(send?.state) === 'not_interested')
    .sort(byLatestSentAt)[0];

  if (normalizedState === 'interested') return interestedSend ?? latestSend;
  if (normalizedState === 'not_interested') return notInterestedSend ?? latestSend;
  return latestSend;
}

function CampaignChips({ sends, preleadState }) {
  if (!sends?.length) return <span className="text-gray-400 text-xs">—</span>;
  const campaignToShow = getRelevantCampaignSend(sends, preleadState);
  const name = campaignToShow?.WhatsAppCampaign?.name ?? '—';
  return (
    <span
      title={name}
      className="inline-flex items-center gap-1 rounded-md bg-customBlue/10 px-2 py-0.5 text-[11px] font-medium text-customBlue max-w-[9rem] truncate">
      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-customBlue/60" />
      <span className="truncate">{name}</span>
    </span>
  );
}

function getAnswerLabel(answer) {
  if (!answer) return '—';
  return (
    answer?.questionOption?.label ||
    answer?.question_option?.label ||
    answer?.raw_text ||
    answer?.raw_payload ||
    '—'
  );
}

function getQuestionOptionLabels(answer) {
  const optionBuckets = [
    answer?.question?.options,
    answer?.question?.questionOptions,
    answer?.question?.question_options,
    answer?.question_options,
    answer?.options
  ];

  const flattenedOptions = optionBuckets.flatMap((bucket) => (Array.isArray(bucket) ? bucket : []));
  const labels = flattenedOptions
    .map((option) =>
      String(
        option?.label ?? option?.option_label ?? option?.text ?? option?.title ?? option?.value ?? ''
      ).trim()
    )
    .filter(Boolean);

  return Array.from(new Set(labels));
}

function formatMetaFieldQuestion(question) {
  if (!question) return '—';
  return String(question)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function FacebookMetaFieldsModal({ preLeadId, onClose }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['preLeadMetaFields', preLeadId],
    queryFn: () => getPreLeadMetaFields(preLeadId),
    enabled: Number.isFinite(preLeadId) && preLeadId > 0
  });

  const fields = data?.fields ?? [];
  const form = data?.form;

  return (
    <Modal width={50} name="Facebook Form Responses" onClose={onClose}>
      <div className="max-h-[70vh] overflow-y-auto space-y-4">
        {isLoading && <p className="text-sm text-gray-500">Loading form responses…</p>}
        {isError && (
          <p className="text-sm text-red-700">
            {error?.response?.data?.message || error?.message || 'Could not load form responses.'}
          </p>
        )}
        {!isLoading && !isError && (
          <>
            {form && (
              <div className="rounded-xl border border-gray-200 bg-gray-50/80 p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Form</p>
                <p className="mt-1 text-sm font-medium text-gray-900">{form.name || '—'}</p>
                {(form.project_name || form.project_id) && (
                  <p className="mt-1 text-xs text-gray-600">
                    Project: {form.project_name || '—'}
                    {form.project_id ? ` (#${form.project_id})` : ''}
                  </p>
                )}
              </div>
            )}
            {fields.length > 0 ? (
              fields.map((field, index) => (
                <div
                  key={field.id ?? `${field.question}-${index}`}
                  className="rounded-xl border border-gray-200 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                  <div className="flex items-start gap-2">
                    <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-customBlue/10 px-1.5 text-[11px] font-semibold text-customBlue">
                      {index + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-gray-500">Field {index + 1}</p>
                      <p className="mt-1 text-sm font-medium text-gray-900">
                        {formatMetaFieldQuestion(field.question)}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Answer</p>
                    <p className="mt-1 text-sm font-medium text-emerald-900 break-words">
                      {field.answer || '—'}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">No form fields available.</p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

function WhatsAppFlowAnswersModal({ selectedPrelead, onClose }) {
  const answers = selectedPrelead?.whatsapp_flow_question_answers ?? [];

  return (
    <Modal width={50} name="WhatsApp Flow Responses" onClose={onClose}>
      <div className="max-h-[70vh] overflow-y-auto space-y-4">
        {answers.length > 0 ? (
          answers
            .slice()
            .sort((a, b) => (a?.question?.question_order ?? 999) - (b?.question?.question_order ?? 999))
            .map((answer, index) => {
              const selectedAnswer = getAnswerLabel(answer);
              const selectedAnswerNorm = selectedAnswer.toLowerCase().trim();
              const otherOptions = getQuestionOptionLabels(answer).filter(
                (label) => label.toLowerCase().trim() !== selectedAnswerNorm
              );

              return (
                <div
                  key={answer.id ?? `${answer.question_id}-${index}`}
                  className="rounded-xl border border-gray-200 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                  <div className="flex items-start gap-2">
                    <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-customBlue/10 px-1.5 text-[11px] font-semibold text-customBlue">
                      {index + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-gray-500">Question {index + 1}</p>
                      <p className="mt-1 text-sm font-medium text-gray-900">{answer?.question?.prompt_text || '—'}</p>
                    </div>
                  </div>

                  <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Selected Answer</p>
                    <p className="mt-1 text-sm font-medium text-emerald-900">{selectedAnswer}</p>
                  </div>

                  <div className="mt-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-customBlue">Other Options</p>
                    {otherOptions.length > 0 ? (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {otherOptions.map((optionLabel) => (
                          <span
                            key={optionLabel}
                            className="inline-flex rounded-full border border-customBlue/20 bg-customBlue/5 px-2.5 py-1 text-xs text-customBlue">
                            {optionLabel}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="mt-1 text-xs text-gray-500">No other options captured.</p>
                    )}
                  </div>
                </div>
              );
            })
        ) : (
          <p className="text-sm text-gray-500">No WhatsApp flow answers available.</p>
        )}
      </div>
    </Modal>
  );
}

function PreleadTable({
  rows,
  showAction = true,
  showFlowResponse = true,
  showFacebookFormResponse = false,
  showWhatsAppCampaign = true,
  onConfirmQualify,
  onConfirmUnqualify,
  onLogAdded,
  isQualifySubmitting = false,
  emptyMessage = 'No preleads found.'
}) {
  const [pendingQualify, setPendingQualify] = useState(null);
  const [selectedPreleadForAnswers, setSelectedPreleadForAnswers] = useState(null);
  const [selectedPreleadForMetaFields, setSelectedPreleadForMetaFields] = useState(null);
  const [selectedPreleadForLogs, setSelectedPreleadForLogs] = useState(null);
  const interestedState = 'interested';

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'phone_display', label: 'Phone' },
    { key: 'email', label: 'Email' },
    { key: 'state', label: 'Status' },
    ...(showWhatsAppCampaign
      ? [{ key: 'whatsapp_campaign_sends', label: 'WhatsApp Campaigns', custom: true }]
      : []),
    { key: 'project_name', label: 'Project Name' },
    ...(showFlowResponse ? [{ key: 'whatsapp_flow_question_answers', label: 'Flow Response', custom: true }] : []),
    ...(showFacebookFormResponse
      ? [{ key: 'facebook_meta_fields', label: 'Form Response', custom: true }]
      : []),
    { key: 'created_at', label: 'Created' },
    { key: 'logs', label: 'Notes', custom: true }
  ];

  const qualifyMessage =
    pendingQualify?.kind === 'qualified'
      ? 'Are you sure you want to mark this prelead as Qualified?'
      : pendingQualify?.kind === 'unqualified'
        ? 'Are you sure you want to mark this prelead as Unqualified?'
        : '';

  const renderActionCell = (row) => {
    const currentState = String(row.state ?? '').toLowerCase();
    const isInterested = currentState === interestedState;
    const hasLead = row.lead_id != null;
    const isUnqualified = row.is_disqualified === true;

    if (!isInterested) return '—';
    if (hasLead) return <div className="text-green-700 font-semibold">Qualified</div>;
    if (isUnqualified) return <div className="text-red-700 font-semibold">Unqualified</div>;

    return (
      <div className="flex flex-wrap items-center justify-center gap-1.5">
        <button
          type="button"
          onClick={() => setPendingQualify({ kind: 'qualified', row })}
          className="inline-flex items-center rounded-lg border border-green-600/80 bg-green-50 px-2.5 py-1 text-[11px] font-semibold text-green-800 hover:bg-green-100 transition-colors">
          Qualified
        </button>
        <button
          type="button"
          onClick={() => setPendingQualify({ kind: 'unqualified', row })}
          className="inline-flex items-center rounded-lg border border-red-600/80 bg-red-50 px-2.5 py-1 text-[11px] font-semibold text-red-800 hover:bg-red-100 transition-colors">
          Unqualified
        </button>
      </div>
    );
  };

  return (
    <>
      <div className="min-h-[24rem] overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="bg-[#E9F5F6] text-customBlue">
            {columns.map((c) => (
              <th
                key={c.key}
                className="px-3 py-2.5 text-left font-semibold whitespace-nowrap border-b border-customBlue/15">
                {c.label}
              </th>
            ))}
            {showAction && (
              <th className="px-3 py-2.5 text-center font-semibold whitespace-nowrap border-b border-customBlue/15">
                Action
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => (
            <tr key={row.id} className="hover:bg-gray-50/80">
              {columns.map((c) =>
                c.custom ? (
                  <td key={c.key} className="px-3 py-2">
                    {c.key === 'whatsapp_campaign_sends' ? (
                      <CampaignChips sends={row[c.key]} preleadState={row.state} />
                    ) : c.key === 'facebook_meta_fields' ? (
                      <button
                        type="button"
                        onClick={() => setSelectedPreleadForMetaFields(row)}
                        className="inline-flex items-center rounded-lg border border-customBlue/40 bg-customBlue/5 px-2.5 py-1 text-[11px] font-semibold text-customBlue hover:bg-customBlue/10 transition-colors">
                        View Form
                      </button>
                    ) : c.key === 'logs' ? (
                      <PreleadNoteButton
                        row={row}
                        onClick={() => setSelectedPreleadForLogs(row)}
                      />
                    ) : Array.isArray(row.whatsapp_flow_question_answers) &&
                      row.whatsapp_flow_question_answers.length > 0 ? (
                      <button
                        type="button"
                        onClick={() => setSelectedPreleadForAnswers(row)}
                        className="inline-flex items-center rounded-lg border border-customBlue/40 bg-customBlue/5 px-2.5 py-1 text-[11px] font-semibold text-customBlue hover:bg-customBlue/10 transition-colors">
                        View Q&A
                      </button>
                    ) : (
                      <span className="text-gray-400 text-xs">—</span>
                    )}
                  </td>
                ) : (
                  <td
                    key={c.key}
                    className="px-3 py-2 text-gray-800 max-w-[12rem] truncate"
                    title={String(row[c.key] ?? '')}>
                    {row[c.key] === null || row[c.key] === undefined || row[c.key] === ''
                      ? '—'
                      : c.key === 'name'
                        ? capitalizeWords(String(row[c.key]))
                        : c.key === 'state'
                          ? formatStatusLabel(String(row[c.key]))
                          : String(row[c.key])}
                  </td>
                )
              )}
              {showAction && (
                <td className="px-3 py-2 text-center">
                  {renderActionCell(row)}
                </td>
              )}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td
                colSpan={columns.length + (showAction ? 1 : 0)}
                className="px-3 py-8 text-center text-sm text-gray-500">
                {emptyMessage}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>

      <PreleadQualifyConfirmModal
        open={Boolean(pendingQualify)}
        kind={pendingQualify?.kind ?? 'qualified'}
        isSubmitting={isQualifySubmitting}
        onClose={() => {
          if (isQualifySubmitting) return;
          setPendingQualify(null);
        }}
        onConfirm={async (reason) => {
          if (!pendingQualify || isQualifySubmitting) return;
          if (pendingQualify.kind === 'qualified') {
            await onConfirmQualify?.(pendingQualify.row);
          } else {
            await onConfirmUnqualify?.(pendingQualify.row, reason);
          }
          setPendingQualify(null);
        }}
        message={qualifyMessage}
      />
      {selectedPreleadForAnswers && (
        <WhatsAppFlowAnswersModal
          selectedPrelead={selectedPreleadForAnswers}
          onClose={() => setSelectedPreleadForAnswers(null)}
        />
      )}
      {selectedPreleadForMetaFields && (
        <FacebookMetaFieldsModal
          preLeadId={selectedPreleadForMetaFields.id}
          onClose={() => setSelectedPreleadForMetaFields(null)}
        />
      )}
      {selectedPreleadForLogs && (
        <PreleadLogModal
          prelead={selectedPreleadForLogs}
          onClose={() => setSelectedPreleadForLogs(null)}
          onLogAdded={onLogAdded}
        />
      )}
    </>
  );
}

const PreleadListDetailPage = () => {
  const PAGE_LIMIT = 20;
  const { listId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { collapsed } = useSidebar();
  const queryClient = useQueryClient();
  const listMeta = location.state || {};

  const [page, setPage] = useState(1);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [isUploadResultModalOpen, setIsUploadResultModalOpen] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [uploadSuccesses, setUploadSuccesses] = useState([]);
  const [uploadDuplicates, setUploadDuplicates] = useState([]);

  const numericListId = Number(listId);
  const selectedFilter = normalizePreleadListFilter(searchParams.get('filter'));
  const filterQuery = useMemo(() => getPreleadListFilterQuery(selectedFilter), [selectedFilter]);
  const filterState = filterQuery.state ?? null;
  const filterIsDisqualified =
    typeof filterQuery.is_disqualified === 'boolean' ? filterQuery.is_disqualified : null;
  const filterConvertedToLead =
    typeof filterQuery.converted_to_lead === 'boolean' ? filterQuery.converted_to_lead : null;

  const setFilterInQuery = (nextFilter) => {
    const normalized = normalizePreleadListFilter(nextFilter);
    const nextParams = new URLSearchParams(searchParams);
    if (!normalized || normalized === 'all') {
      nextParams.delete('filter');
    } else {
      nextParams.set('filter', normalized);
    }
    nextParams.delete('state');
    setSearchParams(nextParams, { replace: true });
  };

  useEffect(() => {
    setPage(1);
  }, [selectedFilter]);

  const {
    data: preleadsData = { overall: null, preLeads: [], meta: { page: 1, limit: PAGE_LIMIT, totalPages: 1 } },
    isLoading,
    isError,
    error,
    isFetching
  } = useQuery({
    queryKey: [
      'preLeadsByListId',
      numericListId,
      page,
      PAGE_LIMIT,
      filterState,
      filterIsDisqualified,
      filterConvertedToLead
    ],
    queryFn: () =>
      getPreLeadsByListId({
        preLeadListId: numericListId,
        page,
        limit: PAGE_LIMIT,
        ...filterQuery
      }),
    enabled: Number.isFinite(numericListId) && numericListId > 0,
    keepPreviousData: true
  });

  const preleads = preleadsData.preLeads;
  const apiList = preleadsData.list;
  const overallStats = preleadsData.overall;
  const title = apiList?.name?.trim() || listMeta.listName || `Prelead list #${listId}`;
  const listSource = apiList?.source ?? listMeta.source ?? '';
  const isFacebookList = String(listSource).trim().toLowerCase() === 'facebook';
  const companyLabel =
    apiList?.company_name?.trim() ||
    (apiList?.company_id != null ? `Company ${apiList.company_id}` : null) ||
    (listMeta.companyId != null && listMeta.companyId !== ''
      ? `Company ${listMeta.companyId}`
      : null);
  const interestedCount = overallStats?.interested_count ?? overallStats?.interested ?? 0;
  const unqualifiedCount = overallStats?.not_interested_count ?? overallStats?.not_interested ?? 0;
  const totalPreLeadsCount = overallStats?.total_pre_leads ?? 0;
  const pendingCount = overallStats?.pending_count ?? overallStats?.pending ?? 0;
  const linkedToLeadCount = overallStats?.linked_to_lead ?? 0;
  const pendingTelecallingCount = overallStats?.pending_telecalling ?? 0;
  const paginationMeta = preleadsData.meta;

  const tableRows = useMemo(
    () =>
      preleads.map((p) => ({
        id: p.id,
        pre_lead_id: p.id,
        name: p.name ?? '',
        countryCode: p.countryCode ?? '',
        phone: p.phone ?? '',
        phone_display: [p.countryCode, p.phone].filter(Boolean).join(' ').trim() || '—',
        email: p.email ?? null,
        state: p.state ?? '—',
        whatsapp_campaign_sends: p.whatsapp_campaign_sends ?? [],
        project_id: p.project_id ?? null,
        project_name: isFacebookList
          ? p.project_name?.trim() || preleadsData.project_name?.trim() || '—'
          : getRelevantCampaignSend(p.whatsapp_campaign_sends, p.state)?.WhatsAppCampaign?.project?.name?.trim() ||
            '—',
        alternate_phone_number: p.alternate_phone_number ?? null,
        min_estimated_budget: p.min_estimated_budget ?? null,
        max_estimated_budget: p.max_estimated_budget ?? null,
        occupation: p.occupation ?? null,
        reference_name: p.reference_name ?? null,
        type: p.type ?? null,
        expected_budget: p.expected_budget ?? null,
        expected_closure_date: p.expected_closure_date ?? null,
        accommodations: p.accommodations ?? [],
        project_accommodations: p.project_accommodations ?? [],
        plotArea: p.plotArea ?? p.plot_area ?? [],
        address: p.address ?? null,
        gender: p.gender ?? null,
        source: p.source ?? null,
        is_disqualified: p.is_disqualified ?? false,
        lead_id: p.lead?.id ?? null,
        whatsapp_flow_question_answers: p.whatsapp_flow_question_answers ?? [],
        reason: p.reason ?? null,
        log: p.log ?? null,
        created_at: formatCellDate(p.created_at)
      })),
    [preleads, isFacebookList, preleadsData.project_name]
  );

  const createPreleadLeadMutation = useMutation({
    mutationFn: (payload) => createLead(payload)
  });
  const unqualifyPreleadMutation = useMutation({
    mutationFn: ({ preLeadId, reason }) => markPreLeadAsUnqualified(preLeadId, reason)
  });

  const handleConfirmQualify = async (row) => {
    if (!row) return;
    if (row.lead_id) {
      navigate(`/builder/sales/${row.lead_id}`);
      return;
    }

    let projectId;
    if (isFacebookList) {
      projectId = Number(row.project_id ?? preleadsData.project_id);
    } else {
      const interestedCampaignSend = row.whatsapp_campaign_sends
        ?.filter((send) => String(send?.state ?? '').trim().toLowerCase() === 'interested')
        .sort((a, b) => new Date(b?.sent_at ?? 0) - new Date(a?.sent_at ?? 0))[0];
      projectId = Number(interestedCampaignSend?.WhatsAppCampaign?.project_id);
    }
    if (!Number.isFinite(projectId) || projectId <= 0) {
      toast.error(
        isFacebookList
          ? 'No project found for this prelead.'
          : 'No interested campaign project found for this prelead.'
      );
      return;
    }

    const mapToIdArray = (items) =>
      Array.isArray(items) ? items.map((item) => item?.id ?? item).filter(Boolean) : [];

    const payload = {
      id: row.id,
      pre_lead_id: row.pre_lead_id ?? row.id,
      name: row.name || `Prelead ${row.id}`,
      countryCode: row.countryCode,
      phone_number: row.phone,
      source: isFacebookList ? 'facebook' : row.source || 'whatsapp_outbound',
      interested_projects: [projectId]
    };

    if (row.email) payload.email = row.email;
    if (row.alternate_phone_number) payload.alternate_phone_number = row.alternate_phone_number;
    if (row.occupation) payload.occupation = row.occupation;
    if (row.reference_name) payload.reference_name = row.reference_name;
    if (row.address) payload.address = row.address;
    if (row.gender) payload.gender = row.gender;
    if (row.type) payload.type = row.type;
    if (row.expected_closure_date) payload.expected_clouser_date = row.expected_closure_date;

    const interestedAccommodationType = mapToIdArray(row.accommodations);
    if (interestedAccommodationType.length) {
      payload.interestedAccomodationType = interestedAccommodationType;
    }

    const projectAccommodationIds = mapToIdArray(row.project_accommodations);
    if (projectAccommodationIds.length) {
      payload.accomodations = projectAccommodationIds;
    }

    const plotAreaIds = mapToIdArray(row.plotArea);
    if (plotAreaIds.length) {
      payload.plotArea = plotAreaIds;
    }

    if (row.min_estimated_budget != null && row.max_estimated_budget != null) {
      payload.expectedBudgetRange = `${row.min_estimated_budget}-${row.max_estimated_budget}`;
    } else if (row.expected_budget != null && Number(row.expected_budget) > 0) {
      payload.expectedBudgetRange = String(row.expected_budget);
    }

    try {
      const result = await createPreleadLeadMutation.mutateAsync(payload);
      const leadId = result?.id ?? result?.data?.id;
      toast.success('Lead created successfully.');
      queryClient.invalidateQueries({ queryKey: ['preLeadsByListId', numericListId] });
      if (leadId) {
        navigate(`/builder/sales/${leadId}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Could not create lead.');
    }
  };

  const handleConfirmUnqualify = async (row, reason) => {
    if (!row?.id) return;
    try {
      await unqualifyPreleadMutation.mutateAsync({ preLeadId: row.id, reason });
      toast.success('Marked as Unqualified.');
      queryClient.invalidateQueries({ queryKey: ['preLeadsByListId', numericListId] });
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Could not update prelead.');
    }
  };

  const handlePreleadLogAdded = () => {
    queryClient.invalidateQueries({ queryKey: ['preLeadsByListId', numericListId] });
  };

  const isMarketing = location.pathname.startsWith('/builder/marketing');

  const goBack = () => {
    if (isMarketing) {
      navigate('/builder/marketing/prelead-lists');
    } else {
      try {
        localStorage.setItem('salesMainSecondaryTab', 'Prelead List');
      } catch {
        /* ignore */
      }
      navigate('/builder/sales/', { state: { secondaryTab: 'Prelead List' } });
    }
  };

  const runDownload = async () => {
    if (!numericListId) return;
    setIsDownloading(true);
    try {
      const buffer = await downloadPreLeadTemplate(numericListId);
      if (!buffer || (buffer.byteLength !== undefined && buffer.byteLength === 0)) {
        toast.error('Downloaded file is empty.');
        return;
      }
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const slug = (title || `list_${numericListId}`).replace(/[^a-zA-Z0-9-_]/g, '_');
      saveAs(blob, `Prelead_${slug}_template.xlsx`);
      toast.success('Template downloaded');
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Download failed');
    } finally {
      setIsDownloading(false);
    }
  };

  const runUpload = async (file) => {
    if (!file || !numericListId) return;
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await uploadPreLeadTemplate(numericListId, formData);
      const { errors, results, duplicates } = normalizeTemplateUploadResponse(response);

      if (errors.length > 0) {
        setUploadErrors(errors);
        setUploadSuccesses(results);
        setUploadDuplicates(duplicates);
        setIsUploadResultModalOpen(true);
        queryClient.invalidateQueries({ queryKey: ['preLeadsByListId', numericListId] });
      } else {
        toast.success('Template uploaded successfully');
        queryClient.invalidateQueries({ queryKey: ['preLeadsByListId', numericListId] });
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const closeUploadResultModal = () => {
    setIsUploadResultModalOpen(false);
    setUploadErrors([]);
    setUploadSuccesses([]);
    setUploadDuplicates([]);
  };

  const handleRejectPreleadDuplicates = (duplicatesToReject) => {
    setUploadDuplicates((prev) =>
      prev.filter((item) => !duplicatesToReject.some((rejected) => rejected.row === item.row))
    );
    if (
      uploadDuplicates.length === duplicatesToReject.length &&
      uploadErrors.length === 0
    ) {
      closeUploadResultModal();
    }
  };

  const handleRetryPreleadUpload = () => {
    closeUploadResultModal();
    requestAnimationFrame(() => fileInputRef.current?.click());
  };

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) runUpload(file);
  };
  return (
    <Layout>
      <UploadResultsModal
        isOpen={isUploadResultModalOpen}
        onClose={closeUploadResultModal}
        uploadSuccesses={uploadSuccesses}
        uploadErrors={uploadErrors}
        uploadDuplicates={uploadDuplicates}
        onAcceptDuplicates={() => {}}
        onRejectDuplicates={handleRejectPreleadDuplicates}
        isProcessingDuplicates={false}
        onRetryUpload={handleRetryPreleadUpload}
        fileEntityLabel="preleads"
        duplicateActionsEnabled={false}
      />

      <div
        className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
        }`}>
        <Header backArrow onBack={goBack} data={{ name: `${isMarketing ? 'Marketing' : 'Sales'} > Prelead lists > ${title}` }} />
      </div>

      <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
        <div className="mt-[4.5rem] min-h-[calc(100vh-5rem)] bg-gray-50 px-2 pb-10 pt-4 sm:px-4 lg:px-6">
          <div className="mx-auto max-w-screen-2xl space-y-4">
            {/* Header + stats combined */}
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
              {/* Top bar: title + buttons */}
              <div className="flex flex-col gap-3 border-b border-gray-100 px-4 py-4 sm:px-5 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-0.5">
                  <p className="text-base font-semibold text-customBlue">{title}</p>
                  {companyLabel != null && (
                    <p className="text-sm text-gray-500">
                      Company{' '}
                      <span className="font-medium text-gray-800">{companyLabel}</span>
                    </p>
                  )}
                </div>
                <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                    className="hidden"
                    onChange={onFileChange}
                  />
                  <button
                    type="button"
                    disabled={isDownloading || isUploading}
                    onClick={runDownload}
                    className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-customBlue bg-white px-3 py-2 text-sm font-semibold text-customBlue hover:bg-[#E9F5F6] disabled:opacity-50 transition-colors sm:w-auto">
                    <Download className="w-4 h-4 shrink-0" />
                    {isDownloading ? 'Downloading…' : 'Download template'}
                  </button>
                  <button
                    type="button"
                    disabled={isDownloading || isUploading}
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-customBlue bg-customBlue px-3 py-2 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-50 transition-opacity sm:w-auto">
                    <Upload className="w-4 h-4 shrink-0" />
                    {isUploading ? 'Uploading…' : 'Upload template'}
                  </button>
                </div>
              </div>

              {/* Summary stats row */}
              <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
                {[
                  {
                    label: 'Total Leads',
                    value: isLoading ? '—' : totalPreLeadsCount,
                    icon: Users,
                    iconBg: 'bg-customBlue/10',
                    iconColor: 'text-customBlue',
                    text: 'text-customBlue'
                  },
                  {
                    label: 'Potential Qualified',
                    value: isLoading ? '—' : interestedCount,
                    icon: TrendingUp,
                    iconBg: 'bg-green-100',
                    iconColor: 'text-green-600',
                    text: 'text-green-700'
                  },
                  {
                    label: 'Not Interested',
                    value: isLoading ? '—' : unqualifiedCount,
                    icon: TrendingDown,
                    iconBg: 'bg-red-100',
                    iconColor: 'text-red-500',
                    text: 'text-red-600'
                  },
                  {
                    label: 'Pending',
                    value: isLoading ? '—' : pendingCount,
                    icon: Clock,
                    iconBg: 'bg-amber-100',
                    iconColor: 'text-amber-500',
                    text: 'text-amber-600'
                  },
                  {
                    label: 'Qualified',
                    value: isLoading ? '—' : linkedToLeadCount,
                    icon: UserCheck,
                    iconBg: 'bg-indigo-100',
                    iconColor: 'text-indigo-600',
                    text: 'text-indigo-700'
                  },
                  {
                    label: 'Pending Telecalling',
                    value: isLoading ? '—' : pendingTelecallingCount,
                    icon: PhoneCall,
                    iconBg: 'bg-purple-100',
                    iconColor: 'text-purple-600',
                    text: 'text-purple-700'
                  }
                ].map(({ label, value, icon: Icon, iconBg, iconColor, text }) => (
                  <div
                    key={label}
                    className="flex min-w-0 items-center gap-3 rounded-lg border border-gray-100 bg-gray-50/40 px-4 py-3">
                    <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${iconBg}`}>
                      <Icon className={`h-4 w-4 ${iconColor}`} />
                    </div>
                    <div>
                      <p className="text-[11px] font-medium leading-none text-gray-500">{label}</p>
                      <p className={`mt-1 text-2xl font-bold leading-none ${text}`}>{value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {isLoading && <p className="text-sm text-gray-600 py-8 text-center">Loading preleads…</p>}

            {isError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                {error?.response?.data?.message || error?.message || 'Could not load preleads.'}
              </div>
            )}

            {!isLoading && !isError && (
              <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                <div className="flex flex-wrap gap-2 border-b border-gray-100 bg-gray-50/50 px-3 py-3">
                  {PRELEAD_LIST_FILTER_TABS.map(({ id, label }) => {
                    const isActive = selectedFilter === id;
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setFilterInQuery(id)}
                        className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${
                          isActive
                            ? 'border-customBlue bg-customBlue text-white'
                            : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                        }`}>
                        {label}
                      </button>
                    );
                  })}
                </div>
                <PreleadTable
                  rows={tableRows}
                  showAction={!isMarketing}
                  showFlowResponse={!isMarketing && !isFacebookList}
                  showFacebookFormResponse={!isMarketing && isFacebookList}
                  showWhatsAppCampaign={!isFacebookList}
                  onConfirmQualify={handleConfirmQualify}
                  onConfirmUnqualify={handleConfirmUnqualify}
                  onLogAdded={handlePreleadLogAdded}
                  emptyMessage={
                    selectedFilter === 'all'
                      ? 'No preleads in this list yet. Download the template, add rows, and upload the file.'
                      : `No preleads found for “${PRELEAD_LIST_FILTER_CONFIG[selectedFilter]?.label ?? 'filter'}”.`
                  }
                  isQualifySubmitting={
                    createPreleadLeadMutation.isPending || unqualifyPreleadMutation.isPending
                  }
                />
              </div>
            )}

            {/* Pagination */}
            {!isLoading && !isError && paginationMeta.totalPages > 1 && (
              <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
                <PaginationComponent
                  page={page}
                  totalPages={paginationMeta.totalPages}
                  onPageChange={(nextPage) => {
                    const boundedPage = Math.max(1, Math.min(paginationMeta.totalPages, Number(nextPage)));
                    setPage(boundedPage);
                  }}
                  isFetching={isFetching}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PreleadListDetailPage;
